<template>
  <!-- Sanne (Figma 3D processing -> Thank you): no result screen; keep the
       Analyzing point cloud up while the measurements come back, then go to
       End (scan-results skipResult). A failed scan still gets the error card. -->
  <video-background v-if="isSanne && !measurementFailed" class="analyzing-root" loop-only>
    <div class="flex flex-col w-full text-center mt-24 px-10">
      <h1 class="screen-info">{{ $t('sanne.generating') }}</h1>
    </div>
  </video-background>
  <body-viewer
    v-else-if="recordToShow && !measurementFailed"
    :record="recordToShow"
    @back="leave"
    @show-record="openRecordViewer"
  />
  <div v-else class="result-root relative w-full flex flex-col">
    <div class="flex-1 relative" :style="containerStyle">
      <model-viewer
        v-if="cachedObjUrl"
        bg-white
        class="h-full"
        :obj-url="cachedObjUrl"
        :shader-type="shaderType"
        :custom-shader="customShader"
        :measurement="currentMeasurement"
      />
    </div>
    <panel v-if="!measurementFailed" :height="panelHeight">
      <div class="w-full h-full text-center purple-bg p-10 flex-centered">
        <p class="scan-result-message" v-html="$t('result.analyzing')"></p>
      </div>
    </panel>
    <rounded-card
      v-else
      show-right-btn
      :head-text="$t('result.oops')"
      :height="panelHeight"
      @set-close-status="leave"
    >
      <error-reasons :subtitle="avoidErrorsText" :tiles="scanErrorTiles" />
      <div v-if="showDoll" class="mini-doll flex flex-col my-5 justify-center">
        <p class="raise-hint" v-html="$t('result.raise')"></p>
        <doll
          :left="left"
          :right="right"
          :left-range="leftRange"
          :right-range="rightRange"
          mini
          left-breathing
          right-breathing
          @left-click="handleLeftClick"
          @right-click="handleRightClick"
          @left-hit="handleLeftHit"
          @right-hit="handleRightHit"
        />
      </div>
      <div v-else-if="!isContactless" class="w-full flex mt-auto mb-5 justify-around">
        <c-button :width="dialogButtonWidth" secondary class="my-3" @click="leave">
          <span>{{ $t('result.leave') }}</span>
        </c-button>
        <c-button v-if="!isMobileScan" :width="dialogButtonWidth" class="my-3" @click="retry">
          <span>{{ $t('result.scan_again') }}</span>
        </c-button>
      </div>
    </rounded-card>
    <leave-button v-if="canLeave" @click="leave" />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';

import audioPlayer from '@/mixins/audio-player';
import scanResults from '@/mixins/scan-results';
import brandMode from '@/mixins/brand-mode';
import breakpoint from '@/mixins/breakpoint';

import ScanProcess from '@/modules/scan-process';

import ModelViewer from '@/components/common/ModelViewer.vue';
import Panel from '@/components/common/Panel.vue';
import Doll from '@/components/common/Doll.vue';
import ErrorReasons from '@/components/common/ErrorReasons.vue';
import errorAccessories from '@/assets/MobileScan/error-accessories.svg';
import errorHair from '@/assets/MobileScan/error-hair.svg';
import errorFeet from '@/assets/MobileScan/error-feet.svg';
import errorPose from '@/assets/MobileScan/error-pose.svg';
import errorAccessoriesSanne from '@/assets/sanne/error-accessories.svg';
import errorHairSanne from '@/assets/sanne/error-hair.svg';
import errorFeetSanne from '@/assets/sanne/error-feet.svg';
import errorPoseSanne from '@/assets/sanne/error-posture.svg';
import LeaveButton from '@/components/common/LeaveButton.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import CButton from '@/components/common/CButton.vue';

import BodyViewer from '@/components/DefaultCloudzet/HomePage/BodyViewer.vue';
import VideoBackground from '@/components/common/VideoBackground.vue';

export default {
  name: 'ShowResult',
  components: {
    LeaveButton,
    BodyViewer,
    Panel,
    ModelViewer,
    RoundedCard,
    ErrorReasons,
    Doll,
    CButton,
    VideoBackground,
  },
  data() {
    return {
      cachedObjUrl: null,
      recordToShow: null,
    };
  },
  mixins: [audioPlayer, scanResults, brandMode, breakpoint],
  mounted() {
    // Cache the original objUrl from user mixin to prevent reload obj
    // The objUrl will be overwritten during getAllKindsOfScanRecords
    this.cachedObjUrl = this.objUrl;
    this.playAudio('16-Almost_there');
  },
  computed: {
    // Sanne ends on the Thank you page instead of showing the measurements
    skipResult() {
      return this.isSanne;
    },
    avoidErrorsText() {
      if (this.isSanne) return this.$t('sanne.avoid_errors');
      return this.$t('result.avoid_errors');
    },
    // the four things a body scan on the scanner trips over, as ErrorReasons tiles
    scanErrorTiles() {
      return [
        {
          icon: this.brandAsset(errorAccessories, errorAccessoriesSanne),
          label: 'result.error_accessories',
        },
        { icon: this.brandAsset(errorHair, errorHairSanne), label: 'result.error_cover_neck' },
        { icon: this.brandAsset(errorFeet, errorFeetSanne), label: 'result.error_floor_marks' },
        { icon: this.brandAsset(errorPose, errorPoseSanne), label: 'result.error_postural' },
      ];
    },
    dialogButtonWidth() {
      // Sanne iPad dialog: two actions side by side at 194 × 45 (Figma); the
      // phone and default layouts keep the 40% pair.
      return this.isSanne && this.isLgViewport ? 194 : '40%';
    },
    ...mapState('app', ['scannersConfigs']),
    ...mapState('mtmUser', {
      mtmShaderType: state => state.shaderType,
      customShader: 'customShader',
    }),
    ...mapState('user', {
      userShaderType: state => state.shaderType,
    }),
    ...mapGetters('mtmUser', {
      hasMtmUserLoggedIn: 'isAuthenticated',
    }),
    ...mapGetters('user', ['latestBodyRecord']),
    shaderType() {
      if (this.hasMtmUserLoggedIn) return this.mtmShaderType;

      if (ScanProcess.scannerId && this.scannersConfigs[ScanProcess.scannerId]) {
        return this.scannersConfigs[ScanProcess.scannerId].shader_type;
      }

      return this.userShaderType;
    },
    measurementLayout() {
      return (!this.isContactless && 'white') || '';
    },
    canLeave() {
      return !this.nothingYet && !this.isContactless && !this.measurementFailed;
    },
    isMobileScan() {
      return ScanProcess.type === 'mobile';
    },
  },
  watch: {
    hasMeasurements(nv) {
      if (this.skipResult) return;
      if (nv) {
        this.playAudio('17-3D_Scan_Successful', true);
        this.getAllKindsOfScanRecords().then(() => {
          this.recordToShow = this.latestBodyRecord;
        });
      }
    },
    measurementFailed(nv, ov) {
      if (nv && !ov && this.isContactless) {
        this.playAudio('18-3D_scan_unsuccessful', true);
      }
    },
  },
  methods: {
    ...mapActions('user', ['getAllKindsOfScanRecords']),
    openRecordViewer(record) {
      this.recordToShow = record;
    },
  },
};
</script>

<style lang="scss" scoped>
// Sanne waiting screen: same stacking as views/Analyzing.vue (type and colour
// come from .analyzing-root in _sanne-layout.scss)
.analyzing-root .screen-info {
  z-index: 1;
}
.result-root {
  background-color: #fff;
}
.purple-bg {
  background-image: var(--brand-gradient-reverse);
  color: #fff;
  .scan-result-message {
    color: #fff;
  }
}
.scan-result-message {
  @apply text-xl font-bold;
  @screen md {
    @apply text-3xl;
    line-height: 60px;
  }
  line-height: 1.67;
}
.mini-doll {
  overflow: hidden;
}
.raise-hint {
  @apply text-2xl text-center px-2 mx-auto my-3;
  color: var(--color-text);
}
</style>
